import { formatCurrencyFromCents } from "@simplyk/common";
import { ToolUIPart } from "ai";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ApprovalContent } from "@/features/Assistant/contexts/AssistantConfigContext";

import { RequestRefundDetails } from "../RequestRefundDetails/RequestRefundDetails";

const REASON_LABEL_KEYS = {
  charged_more_than_once: "donorAssistant.approval.requestRefund.reasons.charged_more_than_once",
  wrong_amount_or_quantity: "donorAssistant.approval.requestRefund.reasons.wrong_amount_or_quantity",
  cannot_attend_or_cancelled: "donorAssistant.approval.requestRefund.reasons.cannot_attend_or_cancelled",
  did_not_mean_to_pay: "donorAssistant.approval.requestRefund.reasons.did_not_mean_to_pay",
  other: "donorAssistant.approval.requestRefund.reasons.other",
} as const;
type RefundRequestReason = keyof typeof REASON_LABEL_KEYS;

const isRefundRequestReason = (value: unknown): value is RefundRequestReason =>
  typeof value === "string" && value in REASON_LABEL_KEYS;

const getRequestRefundInput = (part: ToolUIPart): { transactionId: string; reason: RefundRequestReason } | null =>
  part.type === "tool-requestRefund" &&
  typeof part.input === "object" &&
  part.input !== null &&
  "transactionId" in part.input &&
  typeof part.input.transactionId === "string" &&
  "reason" in part.input &&
  isRefundRequestReason(part.input.reason)
    ? { transactionId: part.input.transactionId, reason: part.input.reason }
    : null;

export const useRequestRefundApproval = (part: ToolUIPart): ApprovalContent | null => {
  const { t } = useTranslate();
  const { locale, isoLocale } = useLocaleContext();
  const input = getRequestRefundInput(part);
  const { data: payment } = trpc.retrieveDonorDashboardTransaction.useQuery(
    { id: input?.transactionId ?? "" },
    { enabled: Boolean(input) }
  );

  if (!input) {
    return null;
  }
  const organizationName = payment?.organization?.name;
  if (!payment || !organizationName) {
    return { description: t("dashboard", "donorAssistant.approval.requestRefund.fallback"), canApprove: false };
  }
  const amount = formatCurrencyFromCents(
    payment.transferrableAmount - (payment.refundedAmount ?? 0) - (payment.inProgressRefundAmount ?? 0),
    {
      isoLocale,
      currency: payment.currency,
      cents: "alwaysVisible",
    }
  );
  const date = new Date(payment.createdAtUtc).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return {
    description: t("dashboard", "donorAssistant.approval.requestRefund.description", { organizationName }),
    details: (
      <RequestRefundDetails
        paymentLabel={`${date} · ${organizationName}`}
        amount={amount}
        reasonLabel={t("dashboard", REASON_LABEL_KEYS[input.reason])}
        organizationName={organizationName}
      />
    ),
    noteLabel: t("dashboard", "donorAssistant.approval.requestRefund.note", { organizationName }),
    approveLabel: t("dashboard", "donorAssistant.approval.requestRefund.submit"),
  };
};
